/** Full-bleed image viewer — MUI Dialog at the DETAIL preset with a link out to
 *  the original. Used by both the read-only viewer and the editor. */
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';

import { imageUrl, IMAGE_PRESET } from '@/utils/imageUrl';

import type { GalleryImage } from './types';

interface Props {
  image: GalleryImage | null;
  onClose: () => void;
}

export function ImageLightbox({ image, onClose }: Props) {
  const src = image ? imageUrl(image.image, IMAGE_PRESET.DETAIL) : null;
  const original = image ? imageUrl(image.image) : null;

  return (
    <Dialog open={image != null} onClose={onClose} maxWidth="lg" fullWidth>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'background.paper' }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 200 }}>
          {src ? (
            // Loads the resized DETAIL variant, never the original, for the preview.
            <Box component="img" src={src} alt={image?.caption ?? 'Image'} sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          ) : (
            <Typography color="text.secondary" sx={{ py: 6 }}>
              No preview available.
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {image?.caption && <Typography variant="body2">{image.caption}</Typography>}
          <Box sx={{ flexGrow: 1 }} />
          {original && (
            <MuiLink
              href={original}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              Open original
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </MuiLink>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
